import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import {
  ListItemButton,
  Button,
  Typography,
  IconButton,
  Stack,
  Tooltip,
  Box,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import FilterAltOffRoundedIcon from '@mui/icons-material/FilterAltOffRounded';
import { ROLES_ICONS } from '../Utils/constants';
import { ColumnFlexBox } from '../../../Components/FlexBox';

const useStyles = () => ({
  root: {
    minWidth: '300px',
  },
});

const UsersFilters = function UsersFilters({
  defaultValues = {
    roleId: null,
    userStatus: null,
  },
  onClose = () => {},
  onChangeFilters,
}) {
  const intl = useIntl();
  const classes = useStyles();

  const [selectedRole, setSelectedRole] = useState(defaultValues?.roleId);
  const [userStatus, setUserStatus] = useState(defaultValues?.userStatus);
  const [withVoipDevice, setWithVoipDevice] = useState(
    defaultValues?.withVoipDevice
  );

  useEffect(() => {
    setSelectedRole(defaultValues?.roleId);
    setUserStatus(defaultValues?.userStatus);
    setWithVoipDevice(defaultValues?.withVoipDevice);
  }, [defaultValues]);

  const handleChangeSelectedStatus = (event, newRole) => {
    //selectedRole can be null if clicks on already selected status
    if (newRole === selectedRole) setSelectedRole(null);
    else setSelectedRole(newRole);
  };

  return (
    <div style={classes.root}>
      <ColumnFlexBox sx={{ mb: '16px' }}>
        <Typography
          variant="caption"
          color="primary"
          sx={{ fontWeight: 'bold', mb: '8px', width: '100%' }}
        >
          {intl.messages.WITH_VOIP_DEVICE}
        </Typography>
        <Stack id="user-status-btns" direction="row" spacing={1}>
          <Tooltip title={intl.messages.ACTIVE} placement="top">
            <IconButton
              id="only-active-btn"
              onClick={() => setWithVoipDevice(true)}
            >
              <CheckCircleRoundedIcon
                sx={{ color: withVoipDevice === true && 'success.main' }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={intl.messages.INACTIVE} placement="top">
            <IconButton
              id="only-disabled-btn"
              onClick={() => setWithVoipDevice(false)}
            >
              <CancelRoundedIcon
                sx={{ color: withVoipDevice === false && 'error.main' }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title={intl.messages.ALL} placement="top">
            <IconButton id="all-btn" onClick={() => setWithVoipDevice(null)}>
              <FilterAltOffRoundedIcon
                sx={{ color: withVoipDevice === null && 'primary.main' }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      </ColumnFlexBox>
      <Typography
        variant="caption"
        color="primary"
        sx={{ fontWeight: 'bold', mb: '8px' }}
      >
        {intl.messages.STATUS_FILTER}
      </Typography>
      <Stack id="user-status-btns" direction="row" spacing={1}>
        <Tooltip title={intl.messages.ACTIVE} placement="top">
          <IconButton id="only-active-btn" onClick={() => setUserStatus(true)}>
            <CheckCircleRoundedIcon
              sx={{ color: userStatus === true && 'success.main' }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={intl.messages.INACTIVE} placement="top">
          <IconButton
            id="only-disabled-btn"
            onClick={() => setUserStatus(false)}
          >
            <CancelRoundedIcon
              sx={{ color: userStatus === false && 'error.main' }}
            />
          </IconButton>
        </Tooltip>
        <Tooltip title={intl.messages.ALL} placement="top">
          <IconButton id="all-btn" onClick={() => setUserStatus(null)}>
            <FilterAltOffRoundedIcon
              sx={{ color: userStatus === null && 'primary.main' }}
            />
          </IconButton>
        </Tooltip>
      </Stack>
      <Typography
        variant="caption"
        color="primary"
        sx={{ fontWeight: 'bold', mb: '8px', mt: '16px' }}
      >
        {intl.messages.ROLE_FILTER}
      </Typography>
      {Object.keys(ROLES_ICONS).map((role) => {
        const roleId = role?.toUpperCase();
        return (
          <ListItemButton
            disableGutters
            sx={{
              display: 'flex',
              pr: '2px',
              pl: '8px',
              justifyContent: 'space-between',
              borderRadius: '5px',
              color: (theme) =>
                selectedRole === roleId && theme.palette.primary.main,
              '&.Mui-selected': {
                background: 'white.main',
                fontWeight: 'bold',
              },
            }}
            key={roleId}
            selected={selectedRole?.toUpperCase() === roleId}
            onClick={(e) => handleChangeSelectedStatus(e, roleId)}
          >
            {intl.messages[roleId]}
            {roleId === selectedRole && <CheckIcon />}
          </ListItemButton>
        );
      })}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          position: 'sticky',
          bottom: 0,
          pt: '16px',
        }}
      >
        <Button
          id="confirm-filters-btn"
          size="large"
          onClick={() => {
            onChangeFilters(selectedRole, userStatus, withVoipDevice);
            onClose();
          }}
        >
          {intl.messages.CONFIRM}
        </Button>
      </Box>
    </div>
  );
};

UsersFilters.propTypes = {
  onChangeFilters: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  onClose: PropTypes.func,
};

export default UsersFilters;
